import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

/**
 * PaymentModeContext (Phase B)
 * Single source of truth for the platform's active payment mode
 * ('platform' | 'host_direct'). Fetched once on mount from
 * GET /api/platform/payment-mode and cached for the app lifetime.
 *
 * Consumers:
 *   const { mode, isHostDirect, loaded } = usePaymentMode();
 *
 * Defaults: mode='platform', isHostDirect=false — matching Phase A behaviour
 * when the endpoint is unreachable so nothing breaks.
 */

const PaymentModeContext = createContext({
  mode: 'platform',
  isHostDirect: false,
  loaded: false
});

export const PaymentModeProvider = ({ children }) => {
  const { api } = useAuth();
  const [mode, setMode] = useState('platform');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/platform/payment-mode');
        if (!cancelled && res?.data?.mode) setMode(res.data.mode);
      } catch (_) {
        // Fall back silently to 'platform'
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  return (
    <PaymentModeContext.Provider
      value={{ mode, isHostDirect: mode === 'host_direct', loaded }}
    >
      {children}
    </PaymentModeContext.Provider>
  );
};

export const usePaymentMode = () => useContext(PaymentModeContext);
